/**
 * useAuraQuickPatch Hook
 *
 * Handles streaming JSON patches for fast, token-efficient HTML editing.
 * Streams patches from the backend and applies them to the current HTML.
 */

import { useState, useCallback, useRef } from "react"
import { useAuth } from "../../../lib/auth-context"
import { applyArrayPatch, SUPPORTED_PATCH_VERSION } from "../utils/patchApplier"
import type { PatchArray } from "../utils/patchApplier"
import { trpc } from "../../../lib/trpc"

// ============================================================================
// TYPES
// ============================================================================

interface QuickPatchMetrics {
  durationMs?: number
  modelId?: string
  patchCount?: number
}

interface UseAuraQuickPatchOptions {
  conversationId: string
  onHtmlUpdate: (html: string) => void
  onPatchComplete?: (versionNumber: number, html: string, messageId: string, metrics?: QuickPatchMetrics) => void
  onError?: (error: string) => void
}

interface UseAuraQuickPatchReturn {
  sendQuickPatch: (content: string, currentHtml: string, modelId?: string) => Promise<void>
  isLoading: boolean
  isStreaming: boolean
  streamingPatches: string
  patchCount: number
  error: string | null
}

// ============================================================================
// HOOK
// ============================================================================

export function useAuraQuickPatch({
  conversationId,
  onHtmlUpdate,
  onPatchComplete,
  onError,
}: UseAuraQuickPatchOptions): UseAuraQuickPatchReturn {
  const { session } = useAuth()

  // State
  const [isLoading, setIsLoading] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [streamingPatches, setStreamingPatches] = useState("")
  const [patchCount, setPatchCount] = useState(0)
  const [error, setError] = useState<string | null>(null)

  // Refs for cleanup
  const abortControllerRef = useRef<AbortController | null>(null)

  // Mutation to save final HTML after patches are applied
  const updateMessageHtmlMutation = trpc.aura.updateMessageHtml.useMutation()

  /**
   * Send a quick patch request and stream the response.
   * Backend sends each patch as a separate SSE event for instant application.
   */
  const sendQuickPatch = useCallback(
    async (content: string, currentHtml: string, modelId?: string) => {
      if (!session?.access_token) {
        setError("Not authenticated")
        onError?.("Not authenticated")
        return
      }

      // Abort any existing stream
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      const abortController = new AbortController()
      abortControllerRef.current = abortController

      setIsLoading(true)
      setIsStreaming(true)
      setStreamingPatches("")
      setPatchCount(0)
      setError(null)

      // Track state for incremental patching
      let workingHtml = currentHtml
      let appliedCount = 0

      try {
        // Use localhost for development - TODO: use env var
        const apiUrl = "http://localhost:8790"

        const response = await fetch(`${apiUrl}/api/aura-chat/edit/stream`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            conversationId,
            content,
            currentHtml,
            modelId,
          }),
          signal: abortController.signal,
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.error || `HTTP error ${response.status}`)
        }

        // Process SSE stream
        const reader = response.body?.getReader()
        if (!reader) {
          throw new Error("No response body")
        }

        const decoder = new TextDecoder()
        let buffer = ""

        // eslint-disable-next-line no-constant-condition -- streaming loop exits via break when done
        while (true) {
          const { done, value } = await reader.read()

          if (done) {
            break
          }

          const chunk = decoder.decode(value, { stream: true })
          buffer += chunk

          // Process complete SSE events
          const lines = buffer.split("\n")
          buffer = lines.pop() || ""

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const data = line.slice(6)

              if (data === "[DONE]") {
                setIsStreaming(false)
                continue
              }

              try {
                const event = JSON.parse(data)

                // Handle patch event - apply immediately
                if (event.type === "patch" && Array.isArray(event.data)) {
                  // Check schema version compatibility
                  if (event.v && event.v !== SUPPORTED_PATCH_VERSION) {
                    console.warn(
                      "[useAuraQuickPatch] Unsupported patch version:",
                      event.v,
                      "expected:",
                      SUPPORTED_PATCH_VERSION,
                    )
                    continue
                  }

                  const patchArray = event.data as PatchArray
                  const result = applyArrayPatch(workingHtml, patchArray)

                  if (result.success) {
                    workingHtml = result.html
                    appliedCount++
                    setPatchCount(appliedCount)
                    onHtmlUpdate(workingHtml) // Update preview immediately
                    setStreamingPatches((prev) => prev + JSON.stringify(patchArray) + "\n")
                  } else {
                    console.warn("[useAuraQuickPatch] Patch failed:", patchArray[0], result.error)
                  }
                  continue
                }

                // Handle done event
                if (event.type === "done") {
                  const metrics: QuickPatchMetrics = {
                    durationMs: event.metrics?.durationMs,
                    modelId: event.metrics?.modelId,
                    patchCount: appliedCount,
                  }

                  // Save the final HTML to the database
                  if (event.messageId && workingHtml) {
                    try {
                      await updateMessageHtmlMutation.mutateAsync({
                        messageId: event.messageId,
                        html: workingHtml,
                      })
                    } catch (saveErr) {
                      console.error("[useAuraQuickPatch] Failed to save HTML:", saveErr)
                      // Don't fail the whole operation, just log the error
                    }
                  }

                  onPatchComplete?.(event.versionNumber, workingHtml, event.messageId, metrics)
                  continue
                }
              } catch {
                // Ignore parse errors for malformed chunks
              }
            }
          }
        }
      } catch (err: any) {
        if (err.name === "AbortError") {
          return
        }

        const errorMessage = err.message || "Failed to apply patches"
        setError(errorMessage)
        onError?.(errorMessage)
      } finally {
        setIsLoading(false)
        setIsStreaming(false)
        abortControllerRef.current = null
      }
    },
    [session?.access_token, conversationId, onHtmlUpdate, onPatchComplete, onError, updateMessageHtmlMutation],
  )

  return {
    sendQuickPatch,
    isLoading,
    isStreaming,
    streamingPatches,
    patchCount,
    error,
  }
}
